import { Injectable } from '@angular/core';
import { ToastService } from 'src/app/@shared/services/toast/toast.service';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { CLOSE } from '../../constants/multi-language.constants';

export interface RouteTrackingBannerOptions {
  ariaLive?: 'polite' | 'assertive' | 'off';
}

/**
 * In-app banner for bus / drop-off tracking messages only.
 * Visually aligned with the side-menu language sheet (handle, white panel, X).
 * Does not use {@link ToastService#showToast} — copy/share toasts stay unchanged.
 */
@Injectable({
  providedIn: 'root',
})
export class RouteTrackingBannerService {
  private host: HTMLDivElement | null = null;

  constructor(
    private readonly toastService: ToastService,
    private readonly languageService: LanguageService
  ) {}

  show(message: string, options?: RouteTrackingBannerOptions): void {
    this.dismiss();

    const ariaLive = options?.ariaLive ?? 'assertive';
    if (ariaLive !== 'off') {
      this.toastService.announce(
        message,
        ariaLive === 'assertive' ? 'assertive' : 'polite'
      );
    }

    const lang = this.languageService.getSelectedLanguage();
    const dir = lang.direction === 'rtl' ? 'rtl' : 'ltr';
    const closeLabel = CLOSE[lang.language];

    const host = document.createElement('div');
    host.className = 'mot-route-tracking-banner-host';
    host.setAttribute('dir', dir);

    const card = document.createElement('div');
    card.className = 'mot-route-tracking-banner';
    card.setAttribute('aria-hidden', 'true');

    const handle = document.createElement('div');
    handle.className = 'mot-route-tracking-banner__handle';

    const body = document.createElement('div');
    body.className = 'mot-route-tracking-banner__body';

    const messageEl = document.createElement('p');
    messageEl.className = 'mot-route-tracking-banner__message';
    messageEl.textContent = message;

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'mot-route-tracking-banner__close';
    closeBtn.setAttribute('aria-label', closeLabel);
    closeBtn.innerHTML =
      '<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>' +
      '</svg>';
    closeBtn.addEventListener('click', () => this.dismiss());

    body.appendChild(messageEl);
    body.appendChild(closeBtn);
    card.appendChild(handle);
    card.appendChild(body);
    host.appendChild(card);
    document.body.appendChild(host);
    this.host = host;

    requestAnimationFrame(() => {
      host.classList.add('mot-route-tracking-banner-host--visible');
    });
  }

  dismiss(): void {
    if (this.host && document.body.contains(this.host)) {
      document.body.removeChild(this.host);
    }
    this.host = null;
  }
}
